<template>
  <div>

    <div v-if="!isFirstRule" class="audiences-operator flex w-full justify-center relative my-5 sm:my-10">

      <div v-if="rule.operator_type === 'or'" class="absolute -mt-4 opacity-40 z-10">exclusive</div>
      <div class="p-2 bg-white inline-block" style="z-index: 5">
        <select-dropdown
          :selected="rule.operator_type"
          :values="['and', 'or']"
          class="text-md w-28"
          hideSelectedItem
          variant="outlined"
          @update:selected="(operator) => { rule.operator_type = operator }"
        />
      </div>
      <hr class="absolute border-0 border-b-1 w-full opacity-30 m-0 inset-y-1/2" />
    </div>

    <div class="form-row mb-2">
      <div>this audience will</div>
      <div>
        <!-- Include / Exclude -->
        <ClosableWrapper :disabled="rules.length <= 1" @close="$emit('deleteRule', rule)">
          <select-dropdown
            :selected="rule.inclusive"
            :values="[
              {label:'include', value:true},
              {label:'exclude', value:false}
            ]"
            class="text-md w-28"
            hideSelectedItem
            variant="outlined"
            @update:selected="(value) => { rule.inclusive = value }"
          />
        </ClosableWrapper>
      </div>
    </div>
    <div class="form-row">
      <div>members who</div>
      <div>
        <!-- Event -->
        <dropdown
          id="event-dropdown"
          :class="{'has-error':!primaryCondition.event_type}"
          :focused.sync="eventsDropdownFocused"
          :headerRows="eventsHeaderRows"
          :rows="eventsRows"
          class="event-dropdown text-md flex-1 w-full"
          variant="outlined"
        >
          <template #label>
            {{ selectedEventSchema ? String(selectedEventSchema.label).toLowerCase() : 'select an attribute' }}
          </template>
        </dropdown>
        <div v-if="selectedEventSchema && [1,2,5,7,8].includes(pattern)" class="flex-1 w-full">
          <!-- Patterns 1-2-5-7-8 : event value -->
          <select-dropdown
            v-if="selectedEventSchema.input_type === 'select' && (this.pattern!==1 || !selectedEventSchema.prefix)"
            id="pattern-1-2-5-7-8-event-value-dropdown"
            :class="{'has-error':primaryConditionValues.value == null}"
            :enableSearch="selectedEventSchema.search"
            :label="eventValueLabel"
            :pagination="(selectedEventSchema.paginate && computedDropdownPagination) || null"
            :selected="primaryConditionValues && primaryConditionValues.value"
            :values="eventValueDropdownItems"
            hideSelectedItem
            class="event-value-dropdown text-md flex-1"
            variant="outlined"
            @focused="onFocusChange"
            @search="onSearch"
            @update:selected="onEventValueSelected"
          />
          <!-- Pattern 8 : date range picker -->
          <div v-if="pattern === 8" class="date-range flex-auto">
            <sb-date
              id="pattern-8-time-picker-min"
              :class="{'has-error':!isPrimaryConditionDateRangeValid}"
              :flatpickr="{ minuteIncrement:60, defaultHour:0, onClose: selectedDates => this.eventRangeMin = selectedDates[0] }"
              :include="['time']"
              :value.sync="primaryConditionValues.range_min"
              class="flex-auto m-0 sb-white-input"
              format="h aa"
              placeholder="start time"
            />
            <span class="text-xl p-1">to</span>
            <sb-date
              id="pattern-8-time-picker-max"
              :class="{'has-error':!isPrimaryConditionDateRangeValid}"
              :flatpickr="{ minuteIncrement:60, defaultHour:0, onClose: selectedDates => this.eventRangeMax = selectedDates[0] }"
              :include="['time']"
              :value.sync="primaryConditionValues.range_max"
              class="flex-auto m-0 sb-white-input"
              format="h aa"
              placeholder="end time"
            />
          </div>
          <!-- Integer range -->
          <div v-if="selectedEventSchema.input_type === 'integer_range'" class="integer-range flex-auto">
            <InputField v-model.number="primaryConditionValues.range_min"
                   id="pattern-7-integer-range-min"
                   :class="{'has-error':!isPrimaryConditionIntegerRangeValid}"
                   :max="primaryConditionValues.range_max ? Math.round(primaryConditionValues.range_max)-1 : integersMaxValue-1"
                   class="flex-auto m-0 sb-input"
                   :min="0"
                   placeholder="min"
                   type="number"
            />
            <span class="p-1 text-xl">to</span>
            <InputField v-model.number="primaryConditionValues.range_max"
                   id="pattern-7-integer-range-max"
                   :class="{'has-error':!isPrimaryConditionIntegerRangeValid}"
                   :max="integersMaxValue"
                   :min="Math.round(primaryConditionValues.range_min)+1" class="flex-auto m-0 sb-input"
                   placeholder="max"
                   type="number"
            />
          </div>
        </div>
        <div class="flex-1 w-full" v-else-if="selectedEventSchema && pattern === 9">
          <!-- Pattern 9 : Date range picker -->
          <date-range
            id="pattern-9-date-range"
            :class="{'has-error':!isPrimaryConditionDateRangeValid}"
            :end.sync="primaryConditionValues.range_max"
            :start.sync="primaryConditionValues.range_min"
            class="date-range-picker p-0 font-primary text-md"
            format="MM/dd/yyyy"
          />
        </div>
        <div v-else class="placeholder flex-1" />
      </div>
    </div>

    <div
      v-if="[1,3,6].includes(pattern) && (selectedEventSchema.input_type !== 'select' || (selectedEventSchema.prefix))"
      class="form-row">
      <div>{{ selectedEventSchema.prefix }}</div>
      <div>
        <!-- Pattern 1/3/6 : event value -->
        <select-dropdown
          v-if="selectedEventSchema.input_type === 'select'"
          id="pattern-1-3-6-event-value-dropdown"
          :class="{'has-error':primaryConditionValues.value == null && !primaryConditionValues.variant}"
          :enableSearch="selectedEventSchema.search"
          :label="eventValueLabel"
          :pagination="selectedEventSchema.paginate && computedDropdownPagination"
          :selected="primaryConditionValues && primaryConditionValues.value"
          :values="eventValueDropdownItems"
          hideSelectedItem
          class="event-value-dropdown text-md flex-1 w-full"
          variant="outlined"
          @focused="onFocusChange"
          @search="onSearch"
          @update:selected="onEventValueSelected"
        />
        <div class="flex-1 w-full">
          <div v-if="pattern === 3" class="flex-1">
            <div v-if="selectedEventValueOption && selectedEventValueOption.input_type === 'integer'"
                 class="flex-1">
              <!-- Pattern 3 : integer -->
              <div class="flex-1">
                <InputField id="pattern-3-integer"
                       v-model.number="primaryConditionValues.value"
                       :class="{'has-error':!isPrimaryConditionIntegerValid}"
                       :max="integersMaxValue"
                       class="sb-input"
                       :min="1"
                       :placeholder="pattern3InputPlaceholder + 's'"
                       type="number"
                       :formatter="pattern3InputFormatter"
                />
              </div>
            </div>
            <div v-else-if="selectedEventValueOption && selectedEventValueOption.input_type === 'integer_range'"
                 class="flex-1">
              <!-- Pattern 3 : integer range min -->
              <InputField id="pattern-3-integer-range-min"
                     v-model.number="primaryConditionValues.range_min"
                     :class="{'has-error':!isPrimaryConditionIntegerRangeValid}"
                     :max="primaryConditionValues.range_max ? Math.round(primaryConditionValues.range_max)-1 : integersMaxValue-1"
                     class="sb-input flex-1 w-0"
                     :min="0"
                     :placeholder="pattern3InputPlaceholder + 's'"
                     type="number"
                     :formatter="pattern3InputFormatter"
              />and
              <!-- Pattern 3 : integer range max -->
              <InputField id="pattern-3-integer-range-max"
                     v-model.number="primaryConditionValues.range_max"
                     :class="{'has-error':!isPrimaryConditionIntegerRangeValid}"
                     :max="integersMaxValue"
                     :min="Math.round(primaryConditionValues.range_min)+1"
                     class="sb-input flex-1 w-0"
                     :placeholder="pattern3InputPlaceholder + 's'"
                     type="number"
                     :formatter="pattern3InputFormatter"
              />
            </div>
          </div>
          <div v-else-if="pattern === 6" class="flex-1">
            <!-- Pattern 6 : integer -->
            <InputField v-if="birthdayFilterValues && birthdayFilterValues.variant === 'days'"
                   id="pattern-6-integer"
                   v-model.number="birthdayFilterValues.value"
                   :class="{'has-error':!isBirthdayFilterIntegerValid}"
                   :max="integersMaxValue"
                   class="sb-input flex-1 w-full"
                   :min="1"
                   placeholder="days"
                   type="number"
                   :formatter="val => pluralizedSuffixFormatter(val, 'day')"
            />
            <!-- Pattern 6 : month dropdown -->
            <select-dropdown
              v-else-if="birthdayFilterValues && birthdayFilterValues.variant === 'month'"
              id="pattern-6-month-dropdown"
              :class="{'has-error':!isBirthdayFilterIntegerValid}"
              :selected="birthdayFilterValues.value"
              :values="[
                {label:'january', value:1},
                {label:'february', value:2},
                {label:'march', value:3},
                {label:'april', value:4},
                {label:'may', value:5},
                {label:'june', value:6},
                {label:'july', value:7},
                {label:'august', value:8},
                {label:'september', value:9},
                {label:'october', value:10},
                {label:'november', value:11},
                {label:'december', value:12}
              ]"
              hideSelectedItem
              class="event-value-dropdown text-md flex-1"
              variant="outlined"
              @focused="onFocusChange"
              @update:selected="(value) => birthdayFilterValues.value = value"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="pattern === 3 && !!frequencyOptions && (primaryConditionValues.value != null || (primaryConditionValues.range_min != null && primaryConditionValues.range_max != null))" class="form-row">
      <div>per</div>
      <div>
        <select-dropdown
          id="pattern-3-frequency-dropdown"
          :class="{'has-error':!isFrequencyValid}"
          :selected="primaryConditionValues.secondary_value"
          :values="getDropdownItemsFromOptions(frequencyOptions)"
          hideSelectedItem
          class="event-value-dropdown text-md flex-1 w-full"
          variant="outlined"
          @focused="onFocusChange"
          @update:selected="(value) => primaryConditionValues.secondary_value = value"
        />
        <div class="placeholder flex-1" />
      </div>
    </div>

    <template
      v-if="pattern === 2 && ((selectedEventValueOption && selectedEventValueOption.key === 'expires') || (selectedEventSchema && selectedEventSchema.input_type === 'hidden'))">
      <div class="form-row">
        <div>{{ selectedEventSchema.prefix }}</div>
        <div>
          <!-- Pattern 2 : variant dropdown -->
          <select-dropdown
            id="pattern-2-variant-dropdown"
            :class="{'has-error':!selectedDateFilterFlowVariant}"
            :label="!selectedDateFilterFlowVariant ? 'select option' : undefined"
            :selected="selectedDateFilterFlowVariant"
            :values="[
              {label:'less than', value:'less_than'},
              {label:'greater than', value:'greater_than'},
              {label:'in between', value:'between'}
            ]"
            hideSelectedItem
            class="event-value-dropdown text-md flex-1 w-full"
            variant="outlined"
            @update:selected="onDateFilterFlowVariantUpdate"
          />
          <!-- Pattern 2 : day/calendar dropdown -->
          <select-dropdown
            v-if="selectedDateFilterFlowVariant"
            id="pattern-2-day-calendar-dropdown"
            :class="{'has-error':!selectedDateFilterFlowInputType}"
            :selected="selectedDateFilterFlowInputType"
            :values="[
              {label:'calendar selection', value:'date'},
              {label:'day selection', value:'day'}
            ]"
            hideSelectedItem
            class="event-value-dropdown text-md flex-1 w-full"
            variant="outlined"
            @update:selected="onDateFilterFlowInputTypeUpdate"
          />
          <div v-else class="placeholder flex-1" />
        </div>
      </div>

      <div class="form-row">
        <div class="placeholder" />
        <div>
          <template v-if="selectedDateFilterFlowInputType === 'day'">
            <div v-if="selectedDateFilterFlowVariant !== 'between'"
                 class="flex-1 w-full">
              <!-- Pattern 2 : integer -->
              <InputField id="pattern-2-integer"
                     v-model.number="dateFilterFlowValues.value"
                     :class="{'has-error':!isDateFilterFlowIntegerValid}"
                     :max="integersMaxValue"
                     class="sb-input flex-1 w-0"
                     :min="1"
                     placeholder="days"
                     type="number"
                     :formatter="val => pluralizedSuffixFormatter(val, 'day')"
              />
            </div>
            <div v-else-if="selectedDateFilterFlowVariant === 'between'"
                 class="flex-1 w-full">
              <!-- Pattern 2 : integer range min -->
              <InputField id="pattern-2-integer-range-min"
                     v-model.number="dateFilterFlowValues.range_min"
                     :class="{'has-error':!isDateFilterFlowIntegerRangeValid}"
                     :max="dateFilterFlowValues.range_max ? Math.round(dateFilterFlowValues.range_max)-1 : integersMaxValue-1"
                     class="sb-input flex-1 w-0"
                     :min="0"
                     placeholder="days"
                     type="number"
                     :formatter="val => pluralizedSuffixFormatter(val, 'day')"
              />
              and
              <!-- Pattern 2 : integer range max -->
              <InputField id="pattern-2-integer-range-max"
                     v-model.number="dateFilterFlowValues.range_max"
                     :class="{'has-error':!isDateFilterFlowIntegerRangeValid}"
                     :max="integersMaxValue"
                     :min="Math.round(dateFilterFlowValues.range_min)+1"
                     class="sb-input flex-1 w-0"
                     placeholder="days"
                     type="number"
                     :formatter="val => pluralizedSuffixFormatter(val, 'day')"
              />
            </div>
          </template>
          <template v-else-if="selectedDateFilterFlowInputType === 'date'">
            <template v-if="selectedDateFilterFlowVariant !== 'between'">
              <!-- Pattern 2 : date picker -->
              <sb-date
                id="pattern-2-date-picker"
                :class="{'has-error':!isDateFilterFlowDateValid}"
                :value.sync="dateFilterFlowValues.value"
                class="sb-white-input flex-1 w-full"
                format="MM/dd/yyyy"
              />
            </template>
            <div v-else-if="selectedDateFilterFlowVariant === 'between'"
                 class="flex-1 w-full">
              <!-- Pattern 2 : date range picker -->
              <date-range
                id="pattern-2-date-range-picker"
                :class="{'has-error':!isDateFilterFlowDateRangeValid}"
                :end.sync="dateFilterFlowValues.range_max"
                :start.sync="dateFilterFlowValues.range_min"
                class="date-range-picker p-0 font-primary text-md"
                format="MM/dd/yyyy"
              />
            </div>
          </template>
          <div class="placeholder flex-1" />
        </div>
      </div>
    </template>

    <div v-if="engagementValues && (pattern === 4 || pattern === 5)" class="form-row">
      <div class="placeholder" />
      <div>
        <div class="flex-1 w-full">
          <select-dropdown
            id="pattern-4-5-dropdown"
            :selected="engagementValues && engagementValues.variant"
            :values="getDropdownItemsFromOptions(engagementOptions)"
            hideSelectedItem
            class="text-md flex-1"
            variant="outlined"
            @update:selected="onEngagementDropdownSelected"
          />
        </div>
        <div class="flex-1 w-full">
          <div v-if="selectedEngagementOption && selectedEngagementOption.input_type === 'integer'" style="flex:2">
            <InputField id="pattern-4-5-integer"
                   v-model.number="engagementValues.value"
                   :class="{'has-error':!isEngagementIntegerValid}"
                   :max="integersMaxValue"
                   :placeholder="'number of '+ engagementValues.variant"
                   class="sb-input"
                   :min="1"
                   type="number"
            />
          </div>
          <div v-else-if="selectedEngagementOption && selectedEngagementOption.input_type === 'integer_range'"
               style="flex:2">
            <!-- Pattern 4-5 : integer range min -->
            <div class="flex-1">
              <InputField id="pattern-4-5-integer-range-min"
                     v-model.number="engagementValues.range_min"
                     :class="{'has-error':!isEngagementIntegerRangeValid}"
                     :max="engagementValues.range_max ? Math.round(engagementValues.range_max)-1 : integersMaxValue-1"
                     class="sb-input"
                     :min="0"
                     placeholder="min"
                     type="number"
              />
            </div>
            <div>
              and
            </div>
            <!-- Pattern 4-5 : integer range max -->
            <div class="flex-1">
              <InputField id="pattern-4-5-integer-range-max"
                     v-model.number="engagementValues.range_max"
                     :class="{'has-error':!isEngagementIntegerRangeValid}"
                     :max="integersMaxValue"
                     :min="Math.round(engagementValues.range_min)+1"
                     class="sb-input"
                     placeholder="max"
                     type="number"
              />
            </div>
          </div>
          <div v-if="selectedEngagementOption && selectedEngagementOption.input_type !== 'select'" class="flex-1">
            {{ (engagementValues.value != null ? engagementValues.value : engagementValues.range_max) | pluralize('time') }}
          </div>
          <!-- Pattern 4-5 : autoconnect dropdown -->
          <select-dropdown
            v-else
            id="pattern-4-5-campaign-dropdown"
            :class="{'has-error':engagementValues.value == null}"
            :enableSearch="selectedEngagementOption.search"
            :label="engagementCampaignDropdownLabel"
            :pagination="selectedEngagementOption.paginate && computedDropdownPagination"
            :selected="engagementValues && engagementValues.value"
            :values="engagementDropdownItems"
            hideSelectedItem
            class="event-value-dropdown text-md flex-1"
            variant="outlined"
            @focused="onFocusChange"
            @search="onSearch"
            @update:selected="onEngagementCampaignDropdownSelected"
          />
        </div>
      </div>
    </div>

    <!-- ADVANCED CONDITIONS -->
    <template v-if="selectedEventSchema">
      <!-- Advanced condition: Total spend -->
      <div v-if="isTotalSpendSelected" class="form-row">
        <div>{{ totalSpendSchema.prefix }}</div>
        <div>
          <div class="flex-1 flex-col sm:flex-row w-full">
            <!-- Pattern TotalSpend : Variant -->
            <ClosableWrapper @close="deleteCondition('total_spend')" class="flex-1 w-full">
              <select-dropdown
                id="pattern-totalspent-variant-dropdown"
                :class="{'has-error':!totalSpendValues.variant}"
                :selected.sync="totalSpendValues.variant"
                :values="getDropdownItemsFromOptions(totalSpendOptions)"
                hideSelectedItem
                class="text-md flex-1"
                variant="outlined"
                @update:selected="resetValues(totalSpendCondition)"
              />
            </ClosableWrapper>
            <div class="flex-1 w-full" v-if="totalSpendValues.variant">
              <!-- Pattern TotalSpend : Integer -->
              <InputField v-if="totalSpendValues.variant !== 'between'"
                     id="pattern-totalspent-integer"
                     v-model.number="totalSpendValues.value"
                     :class="{'has-error':!isTotalSpendIntegerValid}"
                     :max="integersMaxValue"
                     class="sb-input"
                     :min="1"
                     placeholder="$ amount"
                     type="number"
                     :formatter="moneyFormatter"
              />
              <div v-else class="flex-1">
                <!-- Pattern TotalSpend : Integer range min -->
                <InputField id="pattern-totalspent-integer-range-min"
                       v-model.number="totalSpendValues.range_min"
                       :class="{'has-error':!isTotalSpendIntegerRangeValid}"
                       :max="totalSpendValues.range_max ? Math.round(totalSpendValues.range_max)-1 : integersMaxValue-1"
                       class="sb-input flex-1 w-0"
                       :min="0"
                       placeholder="$ amount"
                       type="number"
                       :formatter="moneyFormatter"
                />and
                <!-- Pattern TotalSpend : Integer range max -->
                <InputField id="pattern-totalspent-integer-range-max"
                       v-model.number="totalSpendValues.range_max"
                       :class="{'has-error':!isTotalSpendIntegerRangeValid}"
                       :max="integersMaxValue"
                       :min="Math.round(totalSpendValues.range_min)+1"
                       class="sb-input flex-1 w-0"
                       placeholder="$ amount"
                       type="number"
                       :formatter="moneyFormatter"
                />
              </div>
            </div>
            <div v-else class="placeholder flex-1" />
          </div>
          <div class="flex-1">{{ totalSpendSuffix }}</div>
        </div>
      </div>

      <!-- Advanced condition: Total visits -->
      <div v-if="isTotalVisitsSelected" class="form-row">
        <div>{{ totalVisitsSchema.prefix }}</div>
        <div>
          <div class="flex-1 w-full flex-col sm:flex-row">
            <!-- Pattern TotalVisits : Variant -->
            <ClosableWrapper @close="deleteCondition('total_visits')" class="flex-1 w-full">
              <select-dropdown
                id="pattern-totalvisits-variant-dropdown"
                :class="{'has-error':!totalVisitsValues.variant}"
                :selected.sync="totalVisitsValues.variant"
                :values="getDropdownItemsFromOptions(totalVisitsOptions)"
                hideSelectedItem
                class="text-md flex-1"
                variant="outlined"
                @update:selected="resetValues(totalVisitsCondition)"
              />
            </ClosableWrapper>
            <div class="flex-1 w-full" v-if="totalVisitsValues.variant">
              <!-- Pattern TotalVisits : Integer -->
              <InputField v-if="totalVisitsValues.variant !== 'between'"
                     id="pattern-totalvisits-integer"
                     v-model.number="totalVisitsValues.value"
                     :class="{'has-error':!isTotalVisitsIntegerValid}"
                     :max="integersMaxValue"
                     class="sb-input"
                     :min="1"
                     placeholder="visits"
                     type="number"
                     :formatter="val => pluralizedSuffixFormatter(val, 'visit')"
              />
              <div v-else class="flex-1">
                <!-- Pattern TotalVisits : Integer range min -->
                <InputField id="pattern-totalvisits-integer-range-min"
                       v-model.number="totalVisitsValues.range_min"
                       :class="{'has-error':!isTotalVisitsIntegerRangeValid}"
                       :max="totalVisitsValues.range_max ? Math.round(totalVisitsValues.range_max)-1 : integersMaxValue-1"
                       class="sb-input flex-1 w-0"
                       :min="0"
                       placeholder="visits"
                       type="number"
                       :formatter="val => pluralizedSuffixFormatter(val, 'visit')"
                />and
                <!-- Pattern TotalVisits : Integer range max -->
                <InputField id="pattern-totalvisits-integer-range-max"
                       v-model.number="totalVisitsValues.range_max"
                       :class="{'has-error':!isTotalVisitsIntegerRangeValid}"
                       :max="integersMaxValue"
                       :min="Math.round(totalVisitsValues.range_min)+1"
                       class="sb-input flex-1 w-0"
                       placeholder="visits"
                       type="number"
                       :formatter="val => pluralizedSuffixFormatter(val, 'visit')"
                />
              </div>
            </div>
            <div v-else class="placeholder flex-1" />
          </div>
          <div class="flex-1">{{ totalVisitsSuffix }}</div>
        </div>
      </div>

      <!-- Advanced condition: Time range -->
      <div v-if="isDateFilterSelected" class="form-row">
        <div>{{ dateFilterSchema.prefix }}</div>
        <div class="flex-1 w-full">
          <!-- Pattern TimeRange : Variant -->
          <ClosableWrapper @close="deleteCondition('date_filter')" class="flex-1 w-full">
            <select-dropdown
              id="pattern-timerange-day-calendar-dropdown"
              :class="{'has-error':!dateFilterValues.variant}"
              :selected.sync="dateFilterValues.variant"
              :values="getDropdownItemsFromOptions(dateFilterOptions)"
              hideSelectedItem
              class="text-md flex-1"
              variant="outlined"
              @update:selected="resetValues(dateFilterCondition)"
            />
          </ClosableWrapper>
          <div class="flex-1 h-full w-full" v-if="dateFilterValues.variant">
            <!-- Pattern TimeRange : Integer -->
            <InputField v-if="dateFilterValues.variant === 'days_ago'"
                   id="pattern-timerange-integer"
                   v-model.number="dateFilterValues.value"
                   :class="{'has-error':!isDateFilterIntegerValid}"
                   :max="integersMaxValue"
                   class="sb-input flex-1 w-0"
                   :min="1"
                   placeholder="number of days"
                   type="number"
                   :formatter="val => pluralizedSuffixFormatter(val, 'day')"
            />
            <!-- Pattern TimeRange : Date picker -->
            <date-range
              v-else-if="dateFilterValues.variant === 'date_range'"
              id="pattern-timerange-date-range"
              :class="{'has-error':!isDateFilterDateRangeValid}"
              :end.sync="dateFilterValues.range_max"
              :start.sync="dateFilterValues.range_min"
              class="date-range-picker p-0 font-primary text-md"
              format="MM/dd/yyyy"
            />
          </div>
          <div v-else class="placeholder flex-1" />
        </div>
      </div>

      <div v-if="isAdvancedConditionListAvailable" class="form-row">
        <div @click="advancedBlockExpanded = !advancedBlockExpanded">
          <a class="text-blue cursor-pointer">advanced
            <fai v-if="advancedBlockExpanded" :icon="['fas', 'chevron-up']" style="margin-bottom: -3px;" />
            <fai v-else :icon="['fas', 'chevron-down']" />
          </a>
        </div>
      </div>
      <div v-if="advancedBlockExpanded && isAdvancedConditionListAvailable">
        <chip v-if="isTotalSpendChipVisible"
              class="mr-5"
              @click.native="onTotalSpendChipClick"
        >
          <template #icon>
            <fai :icon="['fas', 'dollar-sign']" />
          </template>
          + define total spend
        </chip>
        <chip v-if="isTotalVisitsChipVisible"
              class="mr-5"
              @click.native="onTotalVisitsChipClick"
        >
          <template #icon>
            <i class="flex before:text-xs flaticon-open-exit-door" />
          </template>
          + define number of visits
        </chip>
        <chip v-if="isDateFilterChipVisible"
              @click.native="onDateFilterChipClick"
        >
          <template #icon>
            <fai :icon="['far', 'clock']" />
          </template>
          + define time range
        </chip>
      </div>
    </template>

  </div>
</template>

<script>
import Chip from "./Chip";
import { BackRow, DateRange, Dropdown, ParentRow, SelectDropdown, SelectRow, InputField, ClosableWrapper,
  pluralizedSuffixFormatter, moneyFormatter } from '@springbig/sb-components';
import { audiencesAPI } from "../../services/api/audiencesAPI";
import { debounce } from "lodash";
import { format } from 'date-fns';

export default {
  name: 'AudienceRule',
  components: {Chip, SelectDropdown, Dropdown, DateRange, InputField, ClosableWrapper},
  props: {
    eventsGroups: {type: Array, default: []},
    rule: Object,
    rules: Array
  },
  data() {
    return {
      dropdownItemsPerPage: 12,
      dropdownFilter: null,
      dropdownPagination: null,
      filteredDropdownValues: null,
      integersMaxValue: 100000,
      selectedDateFilterFlowVariant: null,
      selectedDateFilterFlowInputType: null,
      advancedBlockExpanded: false,
      debouncedFilterDropdownValues: debounce(this.filterDropdownValues, 500),
      eventsRows: [],
      eventsHeaderRows: [],
      defaultEventsRows: this.eventsGroups.map(group =>
        new ParentRow({
          label: String(group.label).toLowerCase(),
          clicked: (row) => {
            this.eventsHeaderRows = [
              new BackRow({
                label: String(row.label).toLowerCase(),
                clicked: () => {
                  this.eventsRows = this.defaultEventsRows;
                  this.eventsHeaderRows = [];
                }
              })
            ];
            this.eventsRows = [
              ...group.options.map(option =>
                new SelectRow({
                  label: String(option.label).toLowerCase(),
                  onSelect: async (row) => {
                    this.primaryCondition.event_type = option.key;
                    const eventValues = {};
                    this.rule.secondary_conditions = this.rule.secondary_conditions
                                                         .filter(condition => condition.id)
                                                         .map(condition => ({...condition, _destroy: true}));
                    this.selectedDateFilterFlowVariant = null;
                    this.selectedDateFilterFlowInputType = null;
                    if (this.selectedEventSchema.input_type === 'hidden') {
                      eventValues.value = this.selectedEventSchema.value;
                      this.onEventValueSelected();
                    }
                    if (this.pattern === 4) {
                      eventValues.variant = 'less_than';
                    }
                    if (this.pattern === 9) {
                      eventValues.range_min = null;
                      eventValues.range_max = null;
                    }
                    this.primaryCondition.event_values = eventValues;
                    await this.updateEventValues();
                    this.eventValueLabel = 'select ' + String(option.label).toLowerCase();
                    this.eventsDropdownFocused = false;
                  }
                })
              )
            ];
          }
        })
      ),
      eventsDropdownFocused: false,
      eventValues: null,
      eventValueLabel: null,
      pattern4Values: null,
      pattern4CampaignDropdownLabel: null,
      pattern5Values: null,
      pattern5VariantDropdownLabel: null,
      pattern5CampaignDropdownLabel: null,
      pattern3InputPlaceholders: {
        'member_data_point_balance': 'point',
        'achieved_event_total_visits': 'visit',
        'achieved_event_visit_frequency': 'visit',
        'achieved_event_average_spend': 'dollar',
        'achieved_event_total_spend': 'dollar',
        'achieved_event_redemption_count_filter': 'redemption'
      },
    };
  },
  computed: {
    pattern() {
      if (this.selectedEventSchema?.secondary_conditions?.some(condition => condition.input_type === 'date_filter_flow')) {
        return 2;
      }
      if (this.selectedEventSchema?.input_type === 'select') {
        if (this.selectedEventSchema.options?.some(option => ['campaign', 'autoconnect'].includes(option.key))) {
          return 4;
        } else if (this.getSecondaryConditionSchema('engagement_filter')) {
          return 5;
        } else if (this.selectedEventSchema.key === 'member_data_birthday') {
          return 6;
        } else if (this.selectedEventSchema.options?.some(option => option.key === 'less_than')) {
          return 3;
        } else {
          return 1;
        }
      } else if (this.selectedEventSchema?.input_type === 'integer_range') {
        return 7;
      } else if (this.selectedEventSchema?.input_type === '12hr_picker_range') {
        return 8;
      } else if (this.selectedEventSchema?.input_type === 'date_range') {
        return 9;
      }
      return 0;
    },
    eventsList() {
      return this.eventsGroups.reduce((options, currentOptions) => [...options, ...currentOptions.options], []);
    },
    isFirstRule() {
      return this.rules.findIndex(r => r === this.rule) === 0;
    },
    computedDropdownPagination() {
      if (!this.dropdownFilter && !this.dropdownPagination) return;
      let filter = {};
      if (this.dropdownFilter && this.filteredDropdownValues) {
        filter.page = Number(this.filteredDropdownValues?.pagination?.current_page ?? 1) - 1;
        filter.pages = Number(this.filteredDropdownValues?.pagination?.total_pages ?? 0);
      }
      return {...this.dropdownPagination, ...filter};
    },

    // Computed dropdowns items
    eventValueDropdownItems() {
      const values = this.filteredDropdownValues?.data || this.eventValues || [];
      return this.getDropdownItemsFromOptions(values);
    },
    pattern4DropdownItems() {
      const values = this.filteredDropdownValues?.data || this.pattern4Values || [];
      return this.getDropdownItemsFromOptions(values);
    },
    pattern5DropdownItems() {
      const values = this.filteredDropdownValues?.data || this.pattern5Values || [];
      return this.getDropdownItemsFromOptions(values);
    },

    secondaryConditionsNotDisabled() {
      return this.rule.secondary_conditions?.filter(c => !c._destroy);
    },

    // Primary condition
    primaryCondition() {
      return this.rule.primary_condition;
    },
    primaryConditionOptions() {
      return this.selectedEventSchema?.options;
    },
    primaryConditionValues() {
      return this.primaryCondition?.event_values;
    },
    isPrimaryConditionIntegerValid() {
      return this.isIntegerValid(this.primaryConditionValues?.value);
    },
    isPrimaryConditionIntegerRangeValid() {
      return this.isIntegerRangeValid(this.primaryConditionValues?.range_min, this.primaryConditionValues?.range_max);
    },
    isPrimaryConditionDateRangeValid() {
      return (
        this.primaryConditionValues?.range_min != null
        && this.primaryConditionValues?.range_max != null
        && this.primaryConditionValues.range_min < this.primaryConditionValues.range_max
      );
    },

    // Birthday filter (pattern 6)
    birthdayFilterValues() {
      return this.getSecondaryConditionValues('birthday_filter')?.event_values;
    },
    isBirthdayFilterIntegerValid() {
      return this.isIntegerValid(this.birthdayFilterValues?.value);
    },

    // Visit frequency (pattery 3)
    frequencyOptions() {
      return this.selectedEventSchema.secondary_options;
    },
    isFrequencyValid() {
      return this.primaryConditionValues?.secondary_value != null;
    },

    // Engagement filter (pattern 5)
    engagementFilterCondition() {
      return this.getSecondaryConditionValues('engagement_filter');
    },
    engagementFilterOptions() {
      return this.getSecondaryConditionSchema('engagement_filter')?.options;
    },
    engagementFilterValues() {
      return this.engagementFilterCondition?.event_values;
    },
    isEngagementFilterIntegerValid() {
      return this.isIntegerValid(this.engagementFilterValues?.value);
    },
    isEngagementFilterIntegerRangeValid() {
      return this.isIntegerRangeValid(this.engagementFilterValues?.range_min, this.engagementFilterValues?.range_max);
    },

    // Date filter flow (pattern 2)
    dateFilterFlowCondition() {
      return this.getSecondaryConditionValues('date_filter_flow')
        || this.getSecondaryConditionValues('expires_filter');
    },
    dateFilterFlowValues() {
      return this.dateFilterFlowCondition?.event_values;
    },
    isDateFilterFlowDateValid() {
      return this.dateFilterFlowValues?.value != null;
    },
    isDateFilterFlowDateRangeValid() {
      return (
        this.dateFilterFlowValues?.range_min != null
        && this.dateFilterFlowValues?.range_max != null
      );
    },
    isDateFilterFlowIntegerValid() {
      return this.isIntegerValid(this.dateFilterFlowValues?.value);
    },
    isDateFilterFlowIntegerRangeValid() {
      return this.isIntegerRangeValid(this.dateFilterFlowValues?.range_min, this.dateFilterFlowValues?.range_max);
    },

    // Advanced condition: Date Filter
    dateFilterCondition() {
      return this.getSecondaryConditionValues('date_filter');
    },
    dateFilterSchema() {
      return this.getSecondaryConditionSchema('date_filter');
    },
    dateFilterOptions() {
      return this.dateFilterSchema?.options;
    },
    dateFilterValues() {
      return this.getSecondaryConditionValues('date_filter')?.event_values;
    },
    isDateFilterAvailable() {
      return !!this.dateFilterSchema ?? false;
    },
    isDateFilterSelected() {
      return this.secondaryConditionsNotDisabled?.some(c => (c.event_type === 'date_filter')) ?? false;
    },
    isDateFilterChipVisible() {
      return this.isDateFilterAvailable && !this.isDateFilterSelected;
    },
    isDateFilterIntegerValid() {
      return this.isIntegerValid(this.dateFilterValues?.value);
    },
    isDateFilterDateRangeValid() {
      return (
        this.dateFilterValues?.range_min != null
        && this.dateFilterValues?.range_max != null
      );
    },

    // Advanced condition: Total spend
    totalSpendCondition() {
      return this.getSecondaryConditionValues('total_spend');
    },
    totalSpendSchema() {
      return this.getSecondaryConditionSchema('total_spend');
    },
    totalSpendOptions() {
      return this.totalSpendSchema?.options;
    },
    totalSpendValues() {
      return this.totalSpendCondition?.event_values;
    },
    totalSpendSuffix() {
      if(!_.isEmpty(this.primaryConditionValues) && !!this.totalSpendValues.variant) {
        const suffixes = {
          'achieved_event_purchased_brand': {prefix: 'on', value: String(this.eventValueLabel).toLowerCase()},
          'achieved_event_purchased_category': {prefix: 'on', value: String(this.eventValueLabel).toLowerCase()},
          'achieved_event_purchased_on_day_of_week': {prefix: 'on', value: String(this.eventValueLabel).toLowerCase()},
          'achieved_event_offer_redeemed': {prefix: 'using', value: String(this.eventValueLabel).toLowerCase()},
          'achieved_event_reward_redeemed': {prefix: 'using', value: String(this.eventValueLabel).toLowerCase()},
          'achieved_event_transaction_location': {prefix: 'in', value: this.eventValueLabel},
          'achieved_event_total_visits': {prefix: 'on', value: 'total visits'},
          'achieved_event_visit_frequency': {prefix: 'on', value: 'each visit'},
          'member_data_last_visit_date': {prefix: 'on', value: 'last visit date'},
        }
        if(Object.keys(suffixes)?.includes(this.primaryCondition.event_type)) {
          return `${suffixes[this.primaryCondition.event_type].prefix} ${suffixes[this.primaryCondition.event_type].value}`;
        } else if (this.primaryConditionValues.range_min && this.primaryConditionValues.range_max) {
          if(this.primaryCondition.event_type === 'achieved_event_purchased_within_date_range') {
            return `within ${format(this.primaryConditionValues.range_min, 'M/d/y')} and ${format(this.primaryConditionValues.range_max, 'M/d/y')}`
          }
          if(this.primaryCondition.event_type === 'achieved_event_purchased_within_hours_of_the_day') {
            return `within ${format(this.primaryConditionValues.range_min, 'ha')} and ${format(this.primaryConditionValues.range_max, 'ha')}`
          }
          if(this.primaryCondition.event_type === 'achieved_event_purchased_within_transaction_range') {
            return `within $${this.primaryConditionValues.range_min} and $${this.primaryConditionValues.range_max}`
          }
        }
      }
    },
    totalVisitsSuffix() {
      if (this.primaryCondition.event_type === 'achieved_event_purchased_brand'
        && !_.isEmpty(this.primaryConditionValues)
        && !!this.totalVisitsValues.variant
      ) {
        return `on ${String(this.eventValueLabel).toLowerCase()} purchase`;
      }
    },
    isTotalSpendAvailable() {
      return !!this.totalSpendSchema ?? false;
    },
    isTotalSpendSelected() {
      return this.secondaryConditionsNotDisabled?.some(c => c.event_type === 'total_spend') ?? false;
    },
    isTotalSpendChipVisible() {
      return this.isTotalSpendAvailable && !this.isTotalSpendSelected;
    },
    isTotalSpendIntegerValid() {
      return this.isIntegerValid(this.totalSpendValues?.value);
    },
    isTotalSpendIntegerRangeValid() {
      return this.isIntegerRangeValid(this.totalSpendValues?.range_min, this.totalSpendValues?.range_max);
    },

    // Advanced condition: Total visits
    totalVisitsOptions() {
      return this.getSecondaryConditionSchema('total_visits')?.options;
    },
    totalVisitsCondition() {
      return this.getSecondaryConditionValues('total_visits');
    },
    totalVisitsValues() {
      return this.getSecondaryConditionValues('total_visits')?.event_values;
    },
    totalVisitsSchema() {
      return this.getSecondaryConditionSchema('total_visits');
    },
    isTotalVisitsAvailable() {
      return !!this.totalVisitsSchema ?? false;
    },
    isTotalVisitsSelected() {
      return this.secondaryConditionsNotDisabled?.some(c => c.event_type === 'total_visits') ?? false;
    },
    isTotalVisitsChipVisible() {
      return this.isTotalVisitsAvailable && !this.isTotalVisitsSelected;
    },
    isTotalVisitsIntegerValid() {
      return this.isIntegerValid(this.totalVisitsValues?.value);
    },
    isTotalVisitsIntegerRangeValid() {
      return this.isIntegerRangeValid(this.totalVisitsValues?.range_min, this.totalVisitsValues?.range_max);
    },

    isAdvancedConditionListAvailable() {
      return (this.selectedEventSchema?.secondary_conditions ?? false)
        && (
          this.isTotalSpendChipVisible
          || this.isTotalVisitsChipVisible
          || this.isDateFilterChipVisible
        );
    },

    selectedEventSchema() {
      return this.eventsList.find(event => event.key === this.primaryCondition.event_type);
    },
    selectedEventValueOption() {
      return this.selectedEventSchema?.options?.find(option => option.key === this.primaryConditionValues?.value || option.key === this.primaryConditionValues?.variant);
    },
    selectedEngagementFilterOption() {
      return this.engagementFilterOptions?.find(o => o.key === this.engagementFilterValues?.variant);
    },

    engagementOptions() {
      if (this.pattern === 4) return this.primaryConditionOptions;
      if (this.pattern === 5) return this.engagementFilterOptions;
    },
    engagementValues() {
      if (this.pattern === 4) return this.primaryConditionValues;
      if (this.pattern === 5) return this.engagementFilterValues;
    },
    engagementCampaignDropdownLabel() {
      if (this.pattern === 4) return this.pattern4CampaignDropdownLabel;
      if (this.pattern === 5) return this.pattern5CampaignDropdownLabel;
    },
    engagementDropdownItems() {
      if (this.pattern === 4) return this.pattern4DropdownItems;
      if (this.pattern === 5) return this.pattern5DropdownItems;
    },
    selectedEngagementOption() {
      if (this.pattern === 4) return this.selectedEventValueOption;
      if (this.pattern === 5) return this.selectedEngagementFilterOption;
    },
    isEngagementIntegerValid() {
      if (this.pattern === 4) return this.isPrimaryConditionIntegerValid;
      if (this.pattern === 5) return this.isEngagementFilterIntegerValid;
    },
    isEngagementIntegerRangeValid() {
      if (this.pattern === 4) return this.isPrimaryConditionIntegerRangeValid;
      if (this.pattern === 5) return this.isEngagementFilterIntegerRangeValid;
    },

    pattern3InputPlaceholder() {
      return this.pattern3InputPlaceholders[this.primaryCondition.event_type];
    },

    pattern3InputFormatter() {
      switch (this.primaryCondition.event_type) {
        case 'achieved_event_average_spend' :
        case 'achieved_event_total_spend' :
          return val => moneyFormatter(val);
        case 'member_data_point_balance' :
        case 'achieved_event_redemption_count_filter' :
        case 'achieved_event_total_visits' :
        case 'achieved_event_visit_frequency' :
          return val => pluralizedSuffixFormatter(val, this.pattern3InputPlaceholder);
      }
    }
  },

  watch: {
    rule: {
      deep: true,
      immediate: true,
      handler() {
        this.rule.is_valid = this.isRuleValid();
      }
    },
    eventsDropdownFocused(isFocused) {
      if (!isFocused) {
        this.eventsRows = this.defaultEventsRows;
        this.eventsHeaderRows = [];
      }
    },
    dropdownFilter(filter, prevFilter) {
      if (filter && JSON.stringify(filter) !== JSON.stringify(prevFilter)) {
        if (prevFilter && filter.page !== prevFilter.page || !prevFilter && filter.page != null && !filter.search) {
          this.filterDropdownValues();
        } else {
          this.debouncedFilterDropdownValues();
        }
      }
    }
  },

  async created() {
    this.eventsRows = this.defaultEventsRows;
    this.eventValueLabel = String(this.selectedEventValueOption?.label || this.primaryConditionValues.value_label).toLowerCase();
    if (this.primaryCondition?.event_type) {
      await this.updateEventValues();
    }
    if (this.pattern === 2 && this.dateFilterFlowValues) {

      const lastUnderscoreIndex = this.dateFilterFlowValues.variant.lastIndexOf('_');
      this.selectedDateFilterFlowVariant = this.dateFilterFlowValues.variant.slice(0, lastUnderscoreIndex);
      this.selectedDateFilterFlowInputType = this.dateFilterFlowValues.variant.slice(lastUnderscoreIndex + 1);
    }
    if (this.pattern === 4 && this.selectedEventValueOption?.input_type === 'select') {
      await this.updatePattern4Values();
      this.pattern4CampaignDropdownLabel = String(this.primaryConditionValues.value_label).toLowerCase();
    }
    if (this.pattern === 5 && this.engagementFilterValues.variant === 'campaign') {
      await this.updatePattern5Values();
      this.pattern5CampaignDropdownLabel = String(this.engagementFilterValues.value_label).toLowerCase();
    }
  },

  methods: {
    isRuleValid() {
      if (!this.selectedEventSchema) return false;
      if (this.selectedEventSchema.input_type === 'select') {
        if (this.primaryConditionValues.value == null && this.primaryConditionValues.variant == null) return false;
      }
      if (this.pattern === 2) {
        if (this.primaryConditionValues?.value == null) return false;
        if (this.primaryConditionValues?.value === 'expires' || this.selectedEventSchema.input_type === 'hidden') {
          if (!this.selectedDateFilterFlowVariant || !this.selectedDateFilterFlowInputType) return false;
          if (this.selectedDateFilterFlowVariant === 'between') {
            if (this.selectedDateFilterFlowInputType === 'day' && !this.isDateFilterFlowIntegerRangeValid) return false;
            if (this.selectedDateFilterFlowInputType === 'date' && !this.isDateFilterFlowDateRangeValid) return false;
          }
          if (this.selectedDateFilterFlowVariant !== 'between') {
            if (this.selectedDateFilterFlowInputType === 'day' && !this.isDateFilterFlowIntegerValid) return false;
            if (this.selectedDateFilterFlowInputType === 'date' && !this.isDateFilterFlowDateValid) return false;
          }
        }
      }
      if (this.pattern === 3 || this.pattern === 4) {
        if (this.selectedEventValueOption?.input_type === 'integer' && !this.isPrimaryConditionIntegerValid) return false;
        if (this.selectedEventValueOption?.input_type === 'integer_range' && !this.isPrimaryConditionIntegerRangeValid) return false;
        if (this.selectedEventValueOption?.input_type === 'select' && this.primaryConditionValues.value == null) return false;
        if (this.frequencyOptions && !this.isFrequencyValid) return false;
      }
      if (this.pattern === 5) {
        if (this.selectedEngagementFilterOption?.input_type === 'integer' && !this.isEngagementFilterIntegerValid) return false;
        if (this.selectedEngagementFilterOption?.input_type === 'integer_range' && !this.isEngagementFilterIntegerRangeValid) return false;
        if (this.selectedEngagementFilterOption?.input_type === 'select' && this.engagementFilterValues.value == null) return false;
      }
      if (this.pattern === 6) {
        if (this.secondaryConditionsNotDisabled.length) {
          if (!this.isBirthdayFilterIntegerValid) return false;
        } else {
          if (this.primaryConditionValues.value == null) return false;
        }
      }
      if (this.pattern === 7 && !this.isPrimaryConditionIntegerRangeValid) return false;
      if (this.pattern === 8 && !this.isPrimaryConditionDateRangeValid) return false;
      if (this.pattern === 9 && !this.isPrimaryConditionDateRangeValid) return false;

      if (this.isTotalSpendSelected) {
        if (this.totalSpendValues.variant == null) return false;
        if (this.totalSpendValues.variant !== 'between' && !this.isTotalSpendIntegerValid) return false;
        if (this.totalSpendValues.variant === 'between' && !this.isTotalSpendIntegerRangeValid) return false;
      }
      if (this.isDateFilterSelected) {
        if (this.dateFilterValues.variant == null) return false;
        if (this.dateFilterValues.variant === 'days_ago' && !this.isDateFilterIntegerValid) return false;
        if (this.dateFilterValues.variant === 'date_range' && !this.isDateFilterDateRangeValid) return false;
      }
      if (this.isTotalVisitsSelected) {
        if (this.totalVisitsValues.variant == null) return false;
        if (this.totalVisitsValues.variant !== 'between' && !this.isTotalVisitsIntegerValid) return false;
        if (this.totalVisitsValues.variant === 'between' && !this.isTotalVisitsIntegerRangeValid) return false;
      }
      return true;
    },
    deleteCondition(eventType) {
      const condition = this.secondaryConditionsNotDisabled.find(condition => condition.event_type === eventType);
      // If the condition is coming from the database it's disabled, otherwise it's removed
      if (condition?.id !== undefined) {
        condition._destroy = true;
      } else {
        this.rule.secondary_conditions = this.secondaryConditionsNotDisabled.filter(condition => condition.event_type !== eventType);
      }
    },
    onFocusChange(isFocused) {
      if (!isFocused) {
        this.filteredDropdownValues = null;
        this.dropdownFilter = null;
      }
    },
    onSearch(search) {
      this.dropdownFilter = {...this.dropdownFilter, search, page: 0};
    },
    onEventValuePaginationChange(page) {
      this.dropdownFilter = {...this.dropdownFilter, page};
    },
    addCondition(eventType, eventValues) {
      const deletedCondition = this.rule.secondary_conditions.find(c => !!c._destroy);
      // If a disabled condition exists, we re-use it to keep the same entry in the DB.
      if (deletedCondition) {
        deletedCondition._destroy = false;
        deletedCondition.event_type = eventType;
        deletedCondition.event_values = eventValues;
      } else {
        this.rule.secondary_conditions.push({
          event_type: eventType,
          event_values: eventValues
        });
      }
    },
    onTotalSpendChipClick() {
      this.addCondition("total_spend", {variant: null});
    },
    onTotalVisitsChipClick() {
      this.addCondition("total_visits", {value: null});
    },
    onDateFilterChipClick() {
      this.addCondition("date_filter", {variant: null});
    },
    onDateFilterFlowVariantUpdate(variant) {
      this.selectedDateFilterFlowVariant = variant;
      if (!this.selectedDateFilterFlowInputType) this.selectedDateFilterFlowInputType = 'day';
      this.setDateFilterFlowVariant(variant + '_' + this.selectedDateFilterFlowInputType);
    },
    onDateFilterFlowInputTypeUpdate(inputType) {
      this.selectedDateFilterFlowInputType = inputType;
      this.setDateFilterFlowVariant(this.selectedDateFilterFlowVariant + '_' + inputType);
    },
    setDateFilterFlowVariant(variant) {
      this.setVariant(variant, this.dateFilterFlowCondition);
    },
    setVariant(variant, condition) {
      condition.event_values.variant = variant;
      this.resetValues(condition);
    },
    resetValues(condition) {
      const props = {};
      if(condition.event_values.variant === 'date_range' || condition.event_values.variant?.split('_')[0] === 'between') {
        props.range_min = null;
        props.range_max = null;
      }else{
        props.value = null;
      }
      if(condition.event_type === 'achieved_event_visit_frequency') {
        props.secondary_value = null
      }
      condition.event_values = {
        variant: condition.event_values.variant,
        ...props
      };
    },
    onPattern4CampaignDropdownSelected(value, label) {
      this.pattern4CampaignDropdownLabel = label.toLowerCase();
      this.primaryConditionValues.value = value;
    },
    onPattern5VariantDropdownSelected(value, label) {
      if (this.engagementFilterValues.variant !== value) {
        this.setVariant(value, this.engagementFilterCondition);
        this.pattern5VariantDropdownLabel = label.toLowerCase();
        if (value === 'campaign') this.updatePattern5Values();
      }
    },
    onPattern5CampaignDropdownSelected(value, label) {
      this.pattern5CampaignDropdownLabel = label.toLowerCase();
      this.engagementFilterValues.value = value;
    },
    onEngagementDropdownSelected(value, label) {
      if (this.pattern === 4) this.onEventValueSelected(value, label);
      if (this.pattern === 5) this.onPattern5VariantDropdownSelected(value, label);
    },
    onEngagementCampaignDropdownSelected(value, label) {
      if (this.pattern === 4) this.onPattern4CampaignDropdownSelected(value, label);
      if (this.pattern === 5) this.onPattern5CampaignDropdownSelected(value, label);
    },
    onEventValueSelected(value, label) {
      this.eventValueLabel = String(label).toLowerCase();
      this.selectedDateFilterFlowVariant = null;
      this.selectedDateFilterFlowInputType = null;
      if (this.pattern === 3 || this.pattern === 4) {
        this.setVariant(value, this.primaryCondition);
      } else {
        this.primaryCondition.event_values = {value};
      }
      if (this.pattern === 4 && this.selectedEventValueOption?.input_type === 'select') {
        this.updatePattern4Values();
      }
      if (this.pattern === 5) {
        if (!this.engagementFilterValues) this.addCondition('engagement_filter', {value: null, variant: 'less_than'});
        if (value === 'campaign') this.updatePattern5Values();
      }
      if (this.pattern === 2) {
        if (
          !this.dateFilterFlowValues
          && (
            this.selectedEventSchema.input_type === 'hidden'
            || this.selectedEventSchema.input_type === 'select' && this.primaryConditionValues?.value === 'expires'
          )
        ) {
          const eventType = this.selectedEventSchema?.secondary_conditions?.find(c => c.input_type === 'date_filter_flow').key;
          this.addCondition(eventType, {value: null, variant: 'less_than_day'});
          this.selectedDateFilterFlowVariant = 'less_than';
          this.selectedDateFilterFlowInputType = 'day';
        } else if (this.primaryConditionValues?.value !== 'expires') {
          this.rule.secondary_conditions = this.rule.secondary_conditions
                                               .filter(condition => condition !== this.dateFilterFlowCondition || condition === this.dateFilterFlowCondition && condition.id)
                                               .map(condition =>
                                                 condition === this.dateFilterFlowCondition && condition.id
                                                   ? {...condition, _destroy: true}
                                                   : condition
                                               );
        }
      } else if (this.pattern === 6) {
        if (['days', 'month'].includes(this.primaryConditionValues?.value)) {
          if (!this.birthdayFilterValues) {
            this.addCondition('birthday_filter', {value: null, variant: this.primaryConditionValues?.value});
          } else {
            this.birthdayFilterValues.variant = this.primaryConditionValues?.value;
            this.birthdayFilterValues.value = null;
          }
        } else {
          this.deleteCondition('birthday_filter');
        }
      }
    },
    async updateEventValues() {
      if (this.selectedEventSchema?.input_type === 'select') {
        if (this.selectedEventSchema?.data_url) {
          const response = (await audiencesAPI.fetchEventValues(this.selectedEventSchema?.data_url));
          this.eventValues = response.data;
          this.dropdownPagination = {
            page: (response.pagination?.current_page ?? 1) - 1,
            pages: response.pagination?.total_pages,
            gap: 2,
            variant: 'dotted',
            updated: this.onEventValuePaginationChange
          };

        } else {
          this.eventValues = this.selectedEventSchema?.options;
          this.dropdownPagination = null;
        }
      } else this.eventValues = null;
    },
    async updatePattern4Values() {
      if (this.selectedEventValueOption?.data_url) {
        const response = (await audiencesAPI.fetchEventValues(this.selectedEventValueOption.data_url));
        this.pattern4Values = response.data;
        this.dropdownPagination = {
          page: (response.pagination?.current_page ?? 1) - 1,
          pages: response.pagination?.total_pages,
          gap: 2,
          variant: 'dotted',
          updated: this.onEventValuePaginationChange
        };
        this.pattern4CampaignDropdownLabel = `select ${String(this.selectedEventValueOption.key).toLowerCase()}`;
      } else {
        this.pattern4CampaignDropdownLabel = null;
        this.dropdownPagination = null;
      }
    },
    async updatePattern5Values() {
      if (this.selectedEngagementFilterOption?.data_url) {
        const response = (await audiencesAPI.fetchEventValues(this.selectedEngagementFilterOption.data_url));
        this.pattern5Values = response.data;
        this.dropdownPagination = {
          page: (response.pagination?.current_page ?? 1) - 1,
          pages: response.pagination?.total_pages,
          gap: 2,
          variant: 'dotted',
          updated: this.onEventValuePaginationChange
        };
        this.pattern5CampaignDropdownLabel = `select campaign`;
      } else {
        this.pattern5CampaignDropdownLabel = null;
        this.dropdownPagination = null;
      }
    },
    isInteger(num) {
      return Number.isInteger(parseFloat(num));
    },
    isIntegerValid(value) {
      return (
        value != null
        && value >= 1
        && this.isInteger(value)
      );
    },
    isIntegerRangeValid(range_min, range_max) {
      return (
        range_min != null
        && range_max != null
        && this.isInteger(range_min)
        && this.isInteger(range_max)
        && parseInt(range_min) < parseInt(range_max)
      );
    },
    getDropdownItemsFromOptions(options) {
      if (!Array.isArray(options)) return [];
      return options.map(option => ({value: option.key, label: String(option.label).toLowerCase()}));
    },
    getSecondaryConditionSchema(key) {
      return this.selectedEventSchema?.secondary_conditions?.find(condition => condition.key === key);
    },
    getSecondaryConditionValues(eventType) {
      return this.secondaryConditionsNotDisabled?.find(condition => condition.event_type === eventType);
    },
    async filterDropdownValues() {
      const filter = this.dropdownFilter;
      let url = this.selectedEventSchema?.data_url;
      url = this.selectedEventSchema?.options?.find(option => ["campaign", "autoconnect"].includes(option.key))?.data_url ?? url;
      url = this.engagementFilterOptions?.find(option => ["campaign", "autoconnect"].includes(option.key))?.data_url ?? url;
      this.filteredDropdownValues = await audiencesAPI.fetchEventValues(url, {
        page: filter.page + 1,
        per_page: this.dropdownItemsPerPage,
        search: filter.search
      });
    },
    pluralizedSuffixFormatter,
    moneyFormatter
  }
};
</script>

<style lang="scss" scoped>
.sb-dropdown::v-deep {
  .sb-rows-container {
    > section.body-rows {
      overflow-y: auto;
      max-height: 355px;
    }
    > section.footer-rows .cursor-pointer {
      padding-inline: 5px;
      &:first-child {
        padding-left: 10px;
      }
      &:last-child {
        padding-right: 10px;
      }
    }
    > section > main {
      &.sb-back-row {
        padding-top: 1rem;
        padding-bottom: 1rem;
        > div:last-child {
          font-weight: normal;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          padding-left: 15px
        }
      }
      line-height: 1rem;
      padding-top: .5rem;
      padding-bottom: .5rem;
    }
  }
  &.has-error {
    .sb-content {
      border-color: red;
    }
  }
}
.sb-date::v-deep {
  .label-container {
    display: flex;
    flex: 1;
  }
  white-space: nowrap;
}
.sb-dropdown,
::v-deep .sb-input,
.sb-date {
  height: inherit;
  background-color: white;
  font-size: 1rem;
  margin: 0;
}
::v-deep .sb-input input {
  border: none;
}
::v-deep .sb-input input,
.sb-date::v-deep .label-container {
  display: flex;
  gap: 4px;
  font-family: AileronLight, sans-serif;
  font-size: 1rem;
  padding: 0.5rem 0.75rem;
  margin: 0;
  line-height: 20px;
  min-width: 80px;
}
.flaticon-open-exit-door:before {
  font-size: inherit;
}
div.form-row {
  display: flex;
  flex-direction: row;
  font-size: 1.25rem;
  padding: 6px 0;
  white-space: nowrap;
  @media only screen and (max-width: 639px) {
    flex-direction: column;
  }
  div {
    gap: .75rem;
    display: flex;
    align-items: center;
  }
  > div {
    &:nth-child(1) {
      width: 150px;
      min-width: 150px;
      margin-right: 10px;
      padding-top: 5px;
      justify-content: end;
      align-items: start;
      @media only screen and (max-width: 639px) {
        justify-content: start;
        padding: 0 0 6px 0;
      }
    }
    &:nth-child(2) {
      flex: 1;
      @media only screen and (max-width: 639px) {
        flex-direction: column;
        align-items: baseline;
      }
    }
  }
}
.date-range-picker::v-deep {
  flex: 1;
  width: auto;
  line-height: 1.25rem;
  .sb-input {
    text-align: left;
    .text-subtitle {
      white-space: nowrap;
      font-size: 1rem;
      color: #cacaca;
      opacity: 1;
    }
  }
}
.date-range-picker.has-error::v-deep > .sb-input,
.sb-date.has-error::v-deep .label-container,
.sb-dropdown.has-error::v-deep .sb-label-container > div,
.sb-dropdown.has-error::v-deep .sb-rows-container,
.sb-input.has-error {
  border-color: red;
  color: red;
}
.field {
  display:block;
  width: 100%;
}
</style>
